import { useNavigate } from 'react-router';
import textsData from '@/textData/DashboardTexts.json';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage } from '@/customHooks';
import type { ContractCategoryKey, ContractCategoryCount } from '@/helpers/contractCategories';
import { theme } from '@/styles';
import { useAllContractsForDashboard } from '@/customHooks';
import { useDashboardFilter } from '@/contexts/DashboardFilterContext';
import { getContractCategoryCounts } from '@/helpers/contractCategories';
import { useErrorHandlerContext } from '@/customHooks';
import { DashboardCardPeriodCaption, LoadingSpinner } from '../atoms';

type DashboardContractsCardProps = {
  data?: ContractCategoryCount[];
};


/** Barras alineadas al significado: fin próximo → alerta; nuevos → marca; inicio próximo → próximo; activos → éxito; no enviado → neutro */
const CATEGORY_COLORS: Record<ContractCategoryKey, string> = {
  endNext30Days: '#E65100',
  newContracts: theme.colors.primary2,
  gettingStartedNext30Days: '#00897B',
  initiated: theme.colors.primary1,
  rentUpdates: '#78909C',
};

const CATEGORY_KEYS: ContractCategoryKey[] = [
  'endNext30Days',
  'newContracts',
  'gettingStartedNext30Days',
  'initiated',
  'rentUpdates',
];

const tenantContractsListPath = `/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.TENANT}`;

export const DashboardContractsCard = ({ data: dataProp }: DashboardContractsCardProps) => {
  const navigate = useNavigate();
  const userLanguage = useUserLanguage();
  const texts =
    userLanguage === 'es' ? textsData.es.contractsCard : textsData.en.contractsCard;

  const { dashboardFilter } = useDashboardFilter();
  const { contracts, loading, error } = useAllContractsForDashboard({ dashboardFilter });

  const { open: errorHandle } = useErrorHandlerContext();
  if (error) errorHandle(error);

  const categoryData = dataProp ?? getContractCategoryCounts(contracts);

  const byKey = categoryData.reduce<Record<ContractCategoryKey, number>>(
    (acc, item) => {
      acc[item.key] = item.count;
      return acc;
    },
    {} as Record<ContractCategoryKey, number>
  );

  const labels: Record<ContractCategoryKey, string> = {
    endNext30Days: texts.endNext30Days,
    newContracts: texts.newContracts,
    gettingStartedNext30Days: texts.gettingStartedNext30Days,
    initiated: texts.initiated,
    rentUpdates: texts.rentUpdates,
  };

  if (loading) {
    return (
      <div className="box-border flex h-full min-h-[360px] flex-col rounded-xl bg-white p-4 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
        <div className="mb-[0.35rem] flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col items-start">
            <h3 className="m-0 shrink-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
            <DashboardCardPeriodCaption tightTop />
          </div>
          <button
            className="border-none bg-transparent p-0 font-[Montserrat,Arial,sans-serif] text-sm font-bold text-[#4A9462] hover:underline"
            type="button"
            onClick={() => navigate(tenantContractsListPath)}
          >
            {texts.seeAll}
          </button>
        </div>
        <hr className="mb-4 mt-0 shrink-0 border-0 border-t border-t-[#E5E7EB]" />
        <div className="flex flex-1 items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="box-border flex h-full min-h-[360px] flex-col rounded-xl bg-white p-4 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
      <div className="mb-[0.35rem] flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col items-start">
          <h3 className="m-0 shrink-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
          <DashboardCardPeriodCaption tightTop />
        </div>
        <button
          className="border-none bg-transparent p-0 font-[Montserrat,Arial,sans-serif] text-sm font-bold text-[#4A9462] hover:underline"
          type="button"
          onClick={() => navigate(tenantContractsListPath)}
        >
          {texts.seeAll}
        </button>
      </div>
      <hr className="mb-4 mt-0 shrink-0 border-0 border-t border-t-[#E5E7EB]" />
      <ul className="m-0 flex flex-1 list-none flex-col gap-1 p-0">
        {CATEGORY_KEYS.map((key, index) => (
          <li
            key={key}
            className={`flex min-h-12 flex-1 items-center gap-3 rounded-md px-2 py-3 ${index === 0 ? 'bg-[#F3F7F3]' : 'bg-transparent'}`}
          >
            <div className="min-w-1 shrink-0 self-stretch rounded-[2px]" style={{ width: 4, backgroundColor: CATEGORY_COLORS[key] }} />
            <span className="flex-1 text-base font-medium text-[#1F2937]">{labels[key]}</span>
            <span className="text-base font-bold text-[#1F2937]">{byKey[key] ?? 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
